import { Box, Icon, Typography } from "@outschool/backpack";
import {
  ClassDetailsNoSectionsQuery,
  ClassDetailsQuery_ActivityFragment,
} from "@outschool/gql-frontend-generated";
import { IconDefinition, faUser, faUsers } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  IconBullet,
  useActivityString,
} from "@outschool/ui-components-website";
import React, { ReactNode } from "react";

export interface InfoBadgeContentProps {
  activity:
    | ClassDetailsNoSectionsQuery["activity"]
    | ClassDetailsQuery_ActivityFragment;
  useIconBullet?: boolean;
}

export function ActivitySize({
  activity,
  useIconBullet = false,
}: InfoBadgeContentProps) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\subcomponents\\ActivityDetails",
  );
  const clubEnrollmentCount = activity.clubSection?.validEnrollmentCount ?? 0;

  const { classSizeString } = useActivityString();
  const count = activity.isClub
    ? clubEnrollmentCount
    : classSizeString(activity);

  const unit = activity.isClub
    ? t("active learner", {
        count: clubEnrollmentCount,
        defaultValue_plural: `active learners`,
      })
    : activity.size_min
    ? t("learner per class", {
        count: !!activity.size_max ? activity.size_max : activity.size_min,
        defaultValue_plural: `learners per class`,
      })
    : null;

  return useIconBullet ? (
    <IconBullet
      icon={activity.size_max == 1 ? faUser : faUsers}
      text={[count, unit].join(" ")}
    />
  ) : (
    <InfoBadge
      icon={activity.size_max == 1 ? faUser : faUsers}
      title={count}
      description={unit}
    />
  );
}

interface InfoBadgeProps {
  icon: IconDefinition;
  title: ReactNode;
  description: ReactNode;
}

export function InfoBadge({ icon, title, description }: InfoBadgeProps) {
  return (
    <Box
      flex
      sx={{
        alignItems: "center",
        gap: "0.5em",
      }}
    >
      <Icon icon={icon} fixedWidth />
      <Box
        flex
        sx={{
          alignItems: "baseline",
          columnGap: "0.5em",
          flexWrap: "wrap",
        }}
      >
        {title && (
          <Typography variant="h4" component="span">
            {title}
          </Typography>
        )}
        {description && (
          <Box
            sx={{
              color: "grey.700",
            }}
          >
            {description}
          </Box>
        )}
      </Box>
    </Box>
  );
}
